import {
  useCreateRoleMutation,
  useRoleBindingNamesForRoleQuery,
  useRoleReportQuery,
  useUpdateRoleMutation
} from "@flanksource-ui/api/query-hooks/useRolesQuery";
import {
  RBAC_DEFAULT_NAMESPACE,
  RBAC_SHIPPED_NAMESPACE,
  getRbacReadOnlyReason,
  qualifiedRbacReference,
  toRbacManifest
} from "@flanksource-ui/api/types/rbacResources";
import {
  RoleBindingUnappliedRules,
  RoleDB,
  RoleDisplay
} from "@flanksource-ui/api/types/roles";
import FormikTextArea from "@flanksource-ui/components/Forms/Formik/FormikTextArea";
import FormikTextInput from "@flanksource-ui/components/Forms/Formik/FormikTextInput";
import { AuthorizationAccessCheck } from "@flanksource-ui/components/Permissions/AuthorizationAccessCheck";
import DeleteRbacObjectButton from "@flanksource-ui/components/Permissions/Rbac/DeleteRbacObjectButton";
import {
  RbacObjectNotInEffect,
  RbacObjectStatus
} from "@flanksource-ui/components/Permissions/Rbac/RbacObjectStatus";
import { RbacObjectYaml } from "@flanksource-ui/components/Permissions/Rbac/RbacObjectYaml";
import { notifyRbacSaved } from "@flanksource-ui/components/Permissions/Rbac/notifyRbacSaved";
import CRDSource from "@flanksource-ui/components/Settings/CRDSource";
import { toastError } from "@flanksource-ui/components/Toast/toast";
import toast from "react-hot-toast";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@flanksource-ui/components/ui/tabs";
import { tables } from "@flanksource-ui/context/UserAccessContext/permissions";
import { Button } from "@flanksource-ui/ui/Buttons/Button";
import { Modal } from "@flanksource-ui/ui/Modal";
import { Form, Formik } from "formik";
import { FaSpinner } from "react-icons/fa";
import RoleRulesList from "../RoleRulesList";
import {
  RoleFormValues,
  formValuesToSpec,
  roleToFormValues,
  validateRoleForm
} from "../roleFormValues";
import RoleRulesForm from "./RoleRulesForm";

function RoleBindingsForRole({ role }: { role: RoleDisplay }) {
  const { data: bindings, isLoading } = useRoleBindingNamesForRoleQuery(
    role.namespace,
    role.name
  );

  if (isLoading) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="form-label">Granted by</label>
      {bindings && bindings.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {bindings.map((binding) => (
            <span
              key={`${binding.namespace}/${binding.name}`}
              className="rounded bg-gray-100 px-1.5 py-0.5 text-sm text-gray-700"
            >
              {bindingLabel(role.namespace, binding)}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">
          No role binding grants this role, so it grants nothing yet.
        </p>
      )}
    </div>
  );
}

// A binding in the role's namespace by its name, and one in another namespace as namespace/name
function bindingLabel(
  roleNamespace: string | null | undefined,
  binding: { namespace?: string; name: string }
) {
  return binding.namespace && binding.namespace !== roleNamespace
    ? qualifiedRbacReference({
        namespace: binding.namespace,
        name: binding.name
      })
    : binding.name;
}

// Rules a binding's constraint can't narrow don't apply through that binding. That's reported, and doesn't
// make the role invalid, so it's shown apart from the role's status.
function UnappliedRulesWarning({
  roleNamespace,
  bindings
}: {
  roleNamespace?: string | null;
  bindings?: RoleBindingUnappliedRules[];
}) {
  if (!bindings?.length) {
    return null;
  }

  return (
    <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
      <p className="font-medium">
        Some rules don&apos;t apply through these role bindings, because their
        constraint can&apos;t narrow them:
      </p>
      <ul className="mt-1 list-disc pl-5">
        {bindings.map((binding) => (
          <li
            key={`${binding.namespace}/${binding.name}`}
            className="break-words"
          >
            <code>{bindingLabel(roleNamespace, binding)}</code>:{" "}
            {binding.rules.join(", ")}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RoleUnappliedRules({ role }: { role: RoleDisplay }) {
  const { data } = useRoleReportQuery(role.namespace, role.name);
  return (
    <UnappliedRulesWarning
      roleNamespace={role.namespace}
      bindings={data?.bindings_with_unapplied_rules}
    />
  );
}

function notifyUnappliedRules(saved: RoleDB) {
  const bindings = saved.bindings_with_unapplied_rules;
  if (!bindings?.length) {
    return;
  }
  toast(
    `Some rules don't apply through role bindings whose constraint can't narrow them: ${bindings
      .map(
        (binding) =>
          `${bindingLabel(saved.namespace, binding)} (${binding.rules.join(", ")})`
      )
      .join("; ")}`,
    { icon: "⚠️", duration: 10000 }
  );
}

type RoleFormProps = {
  isOpen: boolean;
  onClose: () => void;
  data?: RoleDisplay;
};

export default function RoleForm({ isOpen, onClose, data }: RoleFormProps) {
  const readOnlyReason = getRbacReadOnlyReason("roles", data);
  const isReadOnly = !!readOnlyReason;

  const { mutate: create, isLoading: isCreating } = useCreateRoleMutation();
  const { mutate: update, isLoading: isUpdating } = useUpdateRoleMutation();
  const isLoading = isCreating || isUpdating;

  const handleSubmit = (values: RoleFormValues) => {
    // The namespace and name of an existing role can't change
    const manifest = toRbacManifest(
      "roles",
      {
        name: data?.name ?? values.name,
        namespace: data?.namespace ?? values.namespace
      },
      formValuesToSpec(values)
    );

    const mutationOptions = (verb: "created" | "updated") => ({
      onSuccess: (saved: RoleDB) => {
        notifyRbacSaved("roles", verb, saved);
        notifyUnappliedRules(saved);
        onClose();
      },
      onError: (error: unknown) => {
        toastError(error);
      }
    });

    if (data) {
      update(manifest, mutationOptions("updated"));
    } else {
      create(manifest, mutationOptions("created"));
    }
  };

  return (
    <Modal
      title={data ? "Edit Role" : "Add Role"}
      onClose={onClose}
      open={isOpen}
      size="large"
      bodyClass="flex flex-col w-full flex-1 h-full overflow-y-auto"
    >
      <Formik<RoleFormValues>
        initialValues={roleToFormValues(data)}
        onSubmit={handleSubmit}
        validate={validateRoleForm}
      >
        {({ values, isSubmitting }) => {
          const fields = (
            <div className="flex flex-col space-y-4">
              {readOnlyReason && (
                <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
                  <p className="font-medium">{readOnlyReason}</p>
                </div>
              )}

              {data && <RbacObjectNotInEffect {...data} />}

              {data?.rules?.some((rule) => rule.deny) && (
                <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                  <p className="font-medium">
                    This role has deny rules, which aren&apos;t supported
                  </p>
                  <p>
                    Rules only allow.{" "}
                    {isReadOnly
                      ? "Remove the deny rules where this role is defined."
                      : "Remove the deny rules below before saving: they're never saved as allow rules."}
                  </p>
                </div>
              )}

              {data?.namespace && <RoleUnappliedRules role={data} />}

              <div
                className={isReadOnly ? "pointer-events-none opacity-60" : ""}
              >
                <FormikTextInput
                  name="name"
                  label="Name"
                  required
                  // The API identifies a role by namespace and name, so it can't be renamed
                  disabled={isReadOnly || !!data}
                />
              </div>

              <div
                className={
                  isReadOnly || data ? "pointer-events-none opacity-60" : ""
                }
              >
                <FormikTextInput
                  name="namespace"
                  label="Namespace"
                  required
                  placeholder={RBAC_DEFAULT_NAMESPACE}
                  hint={
                    data
                      ? undefined
                      : `Rules can use scopes from any namespace, e.g. ${RBAC_SHIPPED_NAMESPACE}/all-configs. The role can't be in ${RBAC_SHIPPED_NAMESPACE}, which is reserved for what ships with Mission Control.`
                  }
                  // The API identifies a role by namespace and name, so it can't be moved
                  disabled={isReadOnly || !!data}
                />
              </div>

              <div
                className={isReadOnly ? "pointer-events-none opacity-60" : ""}
              >
                <FormikTextArea
                  name="description"
                  label="Description"
                  disabled={isReadOnly}
                />
              </div>

              {isReadOnly ? (
                <RoleRulesList rules={data?.rules ?? []} />
              ) : (
                <RoleRulesForm
                  namespace={values.namespace || RBAC_DEFAULT_NAMESPACE}
                />
              )}

              {data && <RoleBindingsForRole role={data} />}
            </div>
          );

          return (
            <Form className="flex flex-1 flex-col gap-2 overflow-y-auto">
              <div className="flex flex-1 flex-col overflow-y-auto p-4">
                {data ? (
                  <Tabs defaultValue="form" className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <TabsList>
                        <TabsTrigger value="form">Form</TabsTrigger>
                        <TabsTrigger value="yaml">YAML</TabsTrigger>
                      </TabsList>
                      <RbacObjectStatus {...data} />
                    </div>
                    <TabsContent value="form">{fields}</TabsContent>
                    <TabsContent value="yaml">
                      <RbacObjectYaml
                        manifest={toRbacManifest("roles", data, {
                          description: data.description || undefined,
                          rules: data.rules
                        })}
                      />
                    </TabsContent>
                  </Tabs>
                ) : (
                  fields
                )}
              </div>

              <div className="flex items-center justify-between bg-gray-100 px-5 py-4">
                {isReadOnly ? (
                  <CRDSource
                    id={data?.id}
                    source={data?.source}
                    namespace={data?.namespace ?? undefined}
                    name={data?.name}
                  />
                ) : (
                  <>
                    <div>
                      {data?.namespace && (
                        <AuthorizationAccessCheck
                          resource={tables.roles}
                          action="delete"
                        >
                          <DeleteRbacObjectButton
                            resource="roles"
                            namespace={data.namespace}
                            name={data.name}
                            onDeleted={onClose}
                          />
                        </AuthorizationAccessCheck>
                      )}
                    </div>
                    <AuthorizationAccessCheck
                      resource={tables.roles}
                      action={data ? "update" : "create"}
                    >
                      <Button
                        type="submit"
                        text={data ? "Save" : "Create"}
                        className="btn-primary"
                        icon={
                          isLoading ? (
                            <FaSpinner className="animate-spin" />
                          ) : undefined
                        }
                        disabled={isLoading || isSubmitting}
                      />
                    </AuthorizationAccessCheck>
                  </>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}
